/**
 * Day 59 — Restock Intelligence forecast hook.
 *
 * Wraps the forecast_restock RPC (supplemental JSONB analysis blob, no
 * cache write) with prefs-resolved defaults, URL search-param overrides,
 * 250ms debounced persist of tunables to profiles.buying_session_prefs.restock,
 * and a 200ms throttle on explicit refetch() calls.
 *
 * State model:
 *   • distributorId + selectedPath are page-level selections (not persisted)
 *   • forecastDays + growthFactor are user-tunable defaults
 *     that persist to profiles.buying_session_prefs.restock JSONB key
 *   • URL search-params (?forecast=, ?growth=) override
 *     prefs on initial mount only — useful for shareable deep links
 *
 * Auto-fire semantics:
 *   • Mount: fires once with initial params (no debounce gate)
 *   • setParams change: fires after 250ms debounce, alongside the persist
 *   • refetch(): explicit user-triggered, 200ms throttle gates back-to-back
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useApp } from './useApp';
import { mergePrefs } from '../lib/buyingSessionDefaults';
import type { Database, Json } from '../lib/database.types';
import type { BuyingSessionPrefs } from '../types';
import type { RestockVelocitySource } from '../lib/engineFlags';

// U10 A1 W1 re-point — the generated echo of the stored public.marketplace enum
// (values ARE fpc path keys; the alias re-grounds the vocabulary now, the enum
// itself dies at slice B and the generated type regens with it).
type PathSelection = Database['public']['Enums']['marketplace'];

interface RestockParams {
  distributorId: string | null;
  selectedPath: PathSelection;
  forecastDays: number;
  growthFactor: number;
}

/** Per-row product entry in the forecast_restock JSONB blob. Mirrors the
 *  RPC's jsonb_build_object shape exactly (see migration 20260506000001). */
export interface ForecastBlobProduct {
  product_id: string;
  isg_sku: string | null;
  title: string | null;
  on_hand: number;
  on_purchase_order: number;
  monthly_sales: number | null;
  run_rate_units_per_day: number | null;
  effective_run_rate: number | null;
  projected_demand: number | null;
  days_of_stock_remaining: number | null;
  suggested_reorder_qty: number | null;
  lead_time_days: number;
  lead_time_source: 'supplier' | 'default';
  velocity_source: RestockVelocitySource;
  urgency: 'red' | 'yellow' | 'green' | null;
}

/** Top-level forecast_restock JSONB shape. Auto-generated types return
 *  the function as Json — this interface narrows for downstream consumers. */
interface ForecastBlob {
  calculated_at: string;
  params: {
    distributor_id: string;
    // The RPC's own jsonb_build_object key (DB-emitted payload; the stored
    // name renames only with the fn — slice-B family).
    marketplace: string;
    forecast_days: number;
    growth_factor: number;
  };
  default_lead_time_days: number;
  urgency_thresholds: { red_mult: number; yellow_mult: number };
  /** Day 71 P3.4 — CF 49 velocity resolution chain tunables echoed from
   *  business params (velocity_isg_window_days / velocity_isg_min_orders). */
  velocity_chain: { isg_window_days: number; isg_min_orders: number };
  products: ForecastBlobProduct[];
}

export interface RestockForecastResult {
  data: ForecastBlob | null;
  loading: boolean;
  error: string | null;
  params: RestockParams;
  setParams: (patch: Partial<RestockParams>) => void;
  refetch: () => Promise<void>;
}

const DEFAULT_FORECAST_DAYS = 30;
const DEFAULT_GROWTH_FACTOR = 0.0;
const DEFAULT_PATH: PathSelection = 'amazon_fba';
const PERSIST_DEBOUNCE_MS = 250;
const REFETCH_THROTTLE_MS = 200;

/** Clamp to RPC validation window so an out-of-bounds URL param doesn't
 *  produce an immediate RAISE EXCEPTION on the first auto-fire. */
function clampGrowth(g: number): number {
  if (!Number.isFinite(g)) return DEFAULT_GROWTH_FACTOR;
  return Math.min(0.5, Math.max(-0.5, g));
}

function readUrlOverrides(searchParams: URLSearchParams): Partial<RestockParams> {
  const out: Partial<RestockParams> = {};
  const fcRaw = searchParams.get('forecast');
  if (fcRaw !== null) {
    const fc = Number(fcRaw);
    if (Number.isFinite(fc) && fc > 0) out.forecastDays = fc;
  }
  const grRaw = searchParams.get('growth');
  if (grRaw !== null) {
    const gr = Number(grRaw);
    if (Number.isFinite(gr)) out.growthFactor = clampGrowth(gr);
  }
  return out;
}

export function useRestockForecast(): RestockForecastResult {
  const { user, buyingSessionPrefs, setBuyingSessionPrefs } = useApp();
  const [searchParams] = useSearchParams();

  // Initial state: prefs → URL overrides. URL is consulted only on mount.
  const [params, setParamsState] = useState<RestockParams>(() => {
    const prefs = buyingSessionPrefs?.restock || {};
    const base: RestockParams = {
      distributorId: null,
      selectedPath: DEFAULT_PATH,
      forecastDays: prefs.forecastDays ?? DEFAULT_FORECAST_DAYS,
      growthFactor: prefs.growthFactor ?? DEFAULT_GROWTH_FACTOR,
    };
    return { ...base, ...readUrlOverrides(searchParams) };
  });

  const [data, setData] = useState<ForecastBlob | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRefetchAtRef = useRef<number>(0);
  // Capture latest buyingSessionPrefs in a ref so the debounced persist callback
  // sees the current value without re-binding setParams on every prefs change.
  const prefsRef = useRef<BuyingSessionPrefs | null>(buyingSessionPrefs);
  useEffect(() => {
    prefsRef.current = buyingSessionPrefs;
  }, [buyingSessionPrefs]);

  // ── Internal RPC call ────────────────────────────────────────────────────
  const fetchForecast = useCallback(async (p: RestockParams): Promise<void> => {
    if (!p.distributorId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const { data: blob, error: e } = await supabase.rpc('forecast_restock', {
      p_distributor_id: p.distributorId,
      // p_marketplace = the DB fn's param name (stored; renames only with the fn).
      p_marketplace: p.selectedPath,
      p_forecast_days: p.forecastDays,
      p_growth_factor: p.growthFactor,
      p_product_ids: undefined,
    });
    setLoading(false);
    if (e) {
      setError(e.message);
      return;
    }
    setData((blob as unknown as ForecastBlob) ?? null);
  }, []);

  // ── setParams: state update + debounced (persist + auto-refetch) ─────────
  const setParams = useCallback(
    (patch: Partial<RestockParams>) => {
      setParamsState((prev) => {
        const next: RestockParams = { ...prev, ...patch };
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          // Persist tunables only (distributorId + selectedPath are page-state).
          if (user?.id) {
            // Day 98.5 F5 — full-prefs spread via mergePrefs so the debounced
            // restock persist never clobbers saved constraints/guardrails/risk.
            const merged: BuyingSessionPrefs = mergePrefs(prefsRef.current, {
              restock: {
                forecastDays: next.forecastDays,
                growthFactor: next.growthFactor,
              },
            });
            void supabase
              .from('profiles')
              .update({ buying_session_prefs: merged as unknown as Json } as never)
              .eq('id', user.id)
              .then(({ error: e }) => {
                if (e) {
                  console.error('[useRestockForecast] persist error:', e.message);
                } else {
                  setBuyingSessionPrefs(merged);
                }
              });
          }
          // Auto-refetch with the new params.
          void fetchForecast(next);
        }, PERSIST_DEBOUNCE_MS);
        return next;
      });
    },
    [user?.id, setBuyingSessionPrefs, fetchForecast]
  );

  // ── refetch: throttled manual fire ───────────────────────────────────────
  const refetch = useCallback(async (): Promise<void> => {
    const now = Date.now();
    if (now - lastRefetchAtRef.current < REFETCH_THROTTLE_MS) return;
    lastRefetchAtRef.current = now;
    await fetchForecast(params);
  }, [fetchForecast, params]);

  // ── Auto-fire on mount (once) ────────────────────────────────────────────
  useEffect(() => {
    void fetchForecast(params);
    // mount-only by design — subsequent fires happen via setParams's debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Cleanup pending debounce on unmount ──────────────────────────────────
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return { data, loading, error, params, setParams, refetch };
}
